import { useState, useMemo, type ReactNode, type FormEvent } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, BotonAccion, Modal, formatFecha } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';

interface AgendaItem {
  convocatoria_id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  salon_nombre: string;
  cliente_nombre: string;
  direccion: string | null;
  categoria_nombre: string;
  notas: string | null;
  instrucciones: string | null;        // solo para mí
  instrucciones_turno: string | null;  // para todos los del turno
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
// La semana arranca lunes (convención del proyecto).
const DIAS_INICIAL = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

/** "YYYY-MM-DD" de una fecha, armado con las partes locales (no UTC). */
function claveLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function hoyClave(): string {
  return claveLocal(new Date());
}

/** Celdas del mes: null para los huecos antes del día 1, "YYYY-MM-DD" para cada día. */
function celdasDelMes(anio: number, mes: number): Array<string | null> {
  const dowLunes = (new Date(anio, mes, 1).getDay() + 6) % 7; // 0 = lunes
  const cantDias = new Date(anio, mes + 1, 0).getDate();
  const celdas: Array<string | null> = Array(dowLunes).fill(null);
  for (let d = 1; d <= cantDias; d++) celdas.push(claveLocal(new Date(anio, mes, d)));
  return celdas;
}

function lunesActual(): Date {
  const hoy = new Date();
  const dow = hoy.getDay() === 0 ? 7 : hoy.getDay();
  return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (dow - 1));
}

/**
 * Agrupa los servicios que vienen. Los pasados quedan afuera a propósito:
 * la agenda es lo que se viene; lo anterior se mira en Historial o en el
 * calendario, que sí deja navegar meses viejos.
 */
function agrupar(datos: AgendaItem[]): Array<{ titulo: string; items: AgendaItem[] }> {
  const lunes = lunesActual();
  const lunesProx = new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + 7);
  const lunesSig = new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + 14);
  const hoyStr = hoyClave();

  const grupos: Record<string, AgendaItem[]> = { hoy: [], semana: [], proxima: [], despues: [] };
  for (const s of datos) {
    if (s.fecha < hoyStr) continue; // pasado: no es agenda
    const f = new Date(`${s.fecha}T00:00:00`);
    if (s.fecha === hoyStr) grupos.hoy.push(s);
    else if (f >= lunes && f < lunesProx) grupos.semana.push(s);
    else if (f >= lunesProx && f < lunesSig) grupos.proxima.push(s);
    else grupos.despues.push(s);
  }
  const out = [];
  if (grupos.hoy.length) out.push({ titulo: '📍 Hoy', items: grupos.hoy });
  if (grupos.semana.length) out.push({ titulo: 'Esta semana', items: grupos.semana });
  if (grupos.proxima.length) out.push({ titulo: 'Semana que viene', items: grupos.proxima });
  if (grupos.despues.length) out.push({ titulo: 'Más adelante', items: grupos.despues });
  return out;
}

export function MiAgendaPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<AgendaItem[]>('/api/personal/agenda');
  const [cancelando, setCancelando] = useState<AgendaItem | null>(null);
  const [vista, setVista] = useState<'lista' | 'calendario'>('lista');

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;
  if (!datos || datos.length === 0) {
    return <Vacio texto="No tenés servicios confirmados. Cuando aceptes una convocatoria aparecerá acá." />;
  }

  const grupos = agrupar(datos);
  const proximos = grupos.reduce((a, g) => a + g.items.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-neutro-800">Mi agenda</h2>
          <p className="text-sm text-neutro-400">
            {proximos === 0 ? 'Sin servicios próximos' : `${proximos} servicio${proximos !== 1 ? 's' : ''} por delante`}
          </p>
        </div>
        <div className="flex rounded-full border border-contorno-var bg-white p-0.5">
          {(['lista', 'calendario'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition-colors ${
                vista === v ? 'bg-primario-400 text-white' : 'text-neutro-500 hover:text-neutro-700'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {vista === 'lista' ? (
        grupos.length === 0 ? (
          <Vacio texto="No tenés servicios próximos. Pasá al calendario para ver los meses anteriores." />
        ) : (
          grupos.map((g) => (
            <section key={g.titulo}>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-primario-400">{g.titulo}</h3>
              <ul className="space-y-3">
                {g.items.map((a) => (
                  <TarjetaServicio key={a.convocatoria_id} a={a} onCancelar={() => setCancelando(a)} />
                ))}
              </ul>
            </section>
          ))
        )
      ) : (
        <VistaCalendario datos={datos} onCancelar={setCancelando} />
      )}

      {cancelando && (
        <ModalCancelar
          servicio={cancelando}
          onCerrar={() => setCancelando(null)}
          onCancelado={() => { setCancelando(null); recargar(); }}
        />
      )}
    </div>
  );
}

/** Instrucciones del admin: las del turno y, si hay, las propias. */
export function Instrucciones({ turno, individual }: {
  turno: string | null;
  individual: string | null;
}): ReactNode {
  if (!turno && !individual) return null;
  return (
    <div className="mt-2 rounded-lg bg-primario-100 p-2.5">
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-primario-500">📋 Instrucciones</p>
      {turno && <p className="text-xs leading-relaxed text-primario-700">{turno}</p>}
      {individual && (
        <p className={`text-xs leading-relaxed text-primario-700 ${turno ? 'mt-1.5 border-t border-primario-200 pt-1.5' : ''}`}>
          <span className="font-bold">Para vos:</span> {individual}
        </p>
      )}
    </div>
  );
}

function TarjetaServicio({ a, onCancelar }: { a: AgendaItem; onCancelar: () => void }): ReactNode {
  const pasado = a.fecha < hoyClave();
  return (
    <li className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-neutro-800 capitalize">
            {new Date(a.fecha + 'T00:00:00').toLocaleDateString('es-UY', { weekday: 'long' })} {formatFecha(a.fecha)}
          </p>
          <p className="text-sm text-neutro-500">{formatoRango(a.hora_inicio, a.hora_fin)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          pasado ? 'bg-neutro-100 text-neutro-500' : 'bg-estado-ok/15 text-estado-ok'
        }`}>
          {pasado ? 'Ya pasó' : 'Confirmado'}
        </span>
      </div>
      <div className="mt-2 text-sm">
        <p className="font-medium">{a.cliente_nombre} · {a.salon_nombre}</p>
        {a.direccion && <p className="text-xs text-neutro-400">{a.direccion}</p>}
        <p className="mt-1">Puesto: <span className="font-semibold">{a.categoria_nombre}</span></p>
        {a.notas && <p className="mt-1 rounded-lg bg-acento-500/10 p-2 text-xs text-acento-600">⚠ {a.notas}</p>}
        <Instrucciones turno={a.instrucciones_turno} individual={a.instrucciones} />
      </div>
      {!pasado && (
        <button
          className="mt-3 text-xs font-semibold text-estado-error underline hover:no-underline"
          onClick={onCancelar}
        >
          Cancelar servicio
        </button>
      )}
    </li>
  );
}

function VistaCalendario({ datos, onCancelar }: {
  datos: AgendaItem[];
  onCancelar: (a: AgendaItem) => void;
}): ReactNode {
  const hoy = hoyClave();
  const [cursor, setCursor] = useState<{ anio: number; mes: number }>(() => {
    const d = new Date();
    return { anio: d.getFullYear(), mes: d.getMonth() };
  });
  const [diaSel, setDiaSel] = useState<string | null>(hoy);

  // Servicios indexados por fecha, para pintar los puntitos sin recorrer todo.
  const porFecha = useMemo(() => {
    const m: Record<string, AgendaItem[]> = {};
    for (const a of datos) (m[a.fecha] ??= []).push(a);
    return m;
  }, [datos]);

  const celdas = celdasDelMes(cursor.anio, cursor.mes);
  const prefijoMes = `${cursor.anio}-${String(cursor.mes + 1).padStart(2, '0')}`;
  const enElMes = datos.filter((a) => a.fecha.startsWith(prefijoMes)).length;

  function moverMes(delta: number) {
    const d = new Date(cursor.anio, cursor.mes + delta, 1);
    setCursor({ anio: d.getFullYear(), mes: d.getMonth() });
    setDiaSel(null);
  }

  function irAHoy() {
    const d = new Date();
    setCursor({ anio: d.getFullYear(), mes: d.getMonth() });
    setDiaSel(hoy);
  }

  const delDia = diaSel ? (porFecha[diaSel] ?? []) : [];

  return (
    // En escritorio: calendario a la izquierda y detalle del día al lado,
    // para que la grilla no se estire a todo el ancho.
    <div className="space-y-4 md:grid md:grid-cols-[20rem_1fr] md:items-start md:gap-5 md:space-y-0">
      <div className="rounded-2xl border border-contorno-var bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="Mes anterior"
            onClick={() => moverMes(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-contorno-var text-neutro-600 hover:bg-superficie-100"
          >
            ‹
          </button>
          <div className="text-center">
            <p className="text-sm font-bold text-neutro-800">{MESES[cursor.mes]} {cursor.anio}</p>
            <p className="text-xs text-neutro-400">
              {enElMes === 0 ? 'Sin servicios' : `${enElMes} servicio${enElMes !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            type="button"
            aria-label="Mes siguiente"
            onClick={() => moverMes(1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-contorno-var text-neutro-600 hover:bg-superficie-100"
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center">
          {DIAS_INICIAL.map((d, i) => (
            <span key={i} className="py-1 text-[10px] font-bold uppercase text-neutro-400">{d}</span>
          ))}
          {celdas.map((clave, i) => {
            if (!clave) return <span key={`v${i}`} />;
            const tiene = (porFecha[clave]?.length ?? 0) > 0;
            const esHoy = clave === hoy;
            const sel = clave === diaSel;
            return (
              <button
                key={clave}
                type="button"
                onClick={() => setDiaSel(clave)}
                className={`flex flex-col items-center justify-center rounded-lg py-1.5 text-sm transition-colors ${
                  sel
                    ? 'bg-primario-400 font-bold text-white'
                    : esHoy
                      ? 'border border-primario-400 font-semibold text-neutro-800'
                      : tiene
                        ? 'font-semibold text-neutro-800 hover:bg-superficie-100'
                        : 'text-neutro-400 hover:bg-superficie-100'
                }`}
              >
                {Number(clave.slice(8))}
                <span
                  className={`mt-0.5 h-1.5 w-1.5 rounded-full ${
                    tiene ? (sel ? 'bg-white' : 'bg-primario-400') : 'bg-transparent'
                  }`}
                />
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 border-t border-contorno-var pt-3">
          <span className="flex items-center gap-1.5 text-xs text-neutro-400">
            <span className="h-1.5 w-1.5 rounded-full bg-primario-400" /> Día citado
          </span>
          <button
            type="button"
            onClick={irAHoy}
            className="text-xs font-semibold text-primario-400 hover:underline"
          >
            Ir a hoy
          </button>
        </div>
      </div>

      <div>
        {diaSel === null ? (
          <p className="text-center text-sm text-neutro-400 md:text-left">Tocá un día para ver el detalle.</p>
        ) : delDia.length === 0 ? (
          <p className="text-center text-sm text-neutro-400 md:text-left">
            No tenés servicios el {formatFecha(diaSel)}.
          </p>
        ) : (
          <ul className="space-y-3">
            {delDia.map((a) => (
              <TarjetaServicio key={a.convocatoria_id} a={a} onCancelar={() => onCancelar(a)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ModalCancelar({ servicio, onCerrar, onCancelado }: {
  servicio: AgendaItem;
  onCerrar: () => void;
  onCancelado: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function confirmarCancelacion(e: FormEvent) {
    e.preventDefault();
    if (motivo.trim().length < 3) return;
    setEnviando(true);
    try {
      await apiPost(`/api/convocatorias/${servicio.convocatoria_id}/cancelar`, { motivo: motivo.trim() });
      onCancelado();
      alert('Servicio cancelado. Se avisó al administrador y se está buscando un reemplazo.');
    } catch (e2) {
      alert(e2 instanceof Error ? e2.message : 'Error');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal abierto={true} titulo="¿Deseás cancelar este servicio?" onCerrar={onCerrar}>
      <form onSubmit={confirmarCancelacion} className="space-y-3 text-sm">
        <p className="font-semibold text-primario-400">
          {servicio.cliente_nombre} · {servicio.salon_nombre} · {formatFecha(servicio.fecha)} {servicio.hora_inicio}
        </p>
        <p className="text-neutro-500">
          Se le avisa al administrador con tu motivo y se busca un reemplazo automáticamente.
          Si faltan menos de 24 horas, cuenta como cancelación tardía.
        </p>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutro-500">Motivo (obligatorio)</span>
          <textarea
            className="campo"
            rows={3}
            maxLength={300}
            required
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: estoy enfermo, tengo un imprevisto familiar…"
          />
        </label>
        <div className="flex justify-end gap-2">
          <BotonAccion type="button" variante="secundario" onClick={onCerrar} disabled={enviando}>No, volver</BotonAccion>
          <BotonAccion type="submit" variante="peligro" disabled={enviando || motivo.trim().length < 3}>
            {enviando ? 'Cancelando…' : 'Sí, cancelar servicio'}
          </BotonAccion>
        </div>
      </form>
    </Modal>
  );
}
